import React, { useState } from 'react';
import {
  Box,
  Container,
  Grid,
  TextField,
  Button,
  Typography,
  Paper,
  Link,
  Alert,
  CircularProgress,
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { createUser } from '../lib/appwrite';

const Register = () => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    password: '',
    confirmPassword: '',
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    // Validate passwords match
    if (formData.password !== formData.confirmPassword) {
      setError('Las contraseñas no coinciden');
      setLoading(false);
      return;
    }

    // Validate password length
    if (formData.password.length < 8) {
      setError('La contraseña debe tener al menos 8 caracteres');
      setLoading(false);
      return;
    }

    try {
      // Combine first and last name
      const fullName = `${formData.firstName} ${formData.lastName}`.trim();
      
      // Create user account
      const result = await createUser(fullName, formData.email, formData.password);
      
      if (result.success) {
        // Registration successful, redirect to home page
        navigate('/');
      }
    } catch (error) {
      console.error('Registration error:', error);
      
      // Handle specific error cases
      if (error.message.includes('already exists')) {
        setError('Ya existe una cuenta con este email');
      } else if (error.message.includes('invalid')) {
        setError('Email o contraseña inválidos');
      } else {
        setError('Error al crear la cuenta. Por favor, intenta nuevamente.');
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <Box
        sx={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundImage: `url(${require('../assets/bg.jpg')})`,
          backgroundSize: 'cover',
          backgroundPosition: 'bottom',
          backgroundRepeat: 'no-repeat',
          zIndex: -1,
          height: '100hv',
        }}
      />
      <Container maxWidth="lg" sx={{ height: '100%', py: 4 }}>
        <Grid container spacing={2} sx={{ height: '100%', minHeight: 'calc(100vh - 200px)' }}>
          {/* Left side - Logo */}
          <Grid item xs={12} md={6}>
            <Box
              sx={{
                height: '100%',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'space-between',
                bgcolor: 'rgba(0, 0, 0, 0.7)',
                borderRadius: 2,
                p: 4,
                gap: 2,
              }}
            >
              <Typography
                variant="h3"
                component="h1"
                sx={{
                  color: 'white',
                  fontWeight: 'bold',
                  textAlign: 'center',
                  whiteSpace: 'pre-line',
                  mb: 2,
                }}
              >
                Impulsando el deporte{'\n'}
                que{' '}
                <Typography
                  component="span"
                  variant="h3"
                  sx={{
                    color: 'secondary.main',
                    fontWeight: 'bold',
                  }}
                >
                  amamos
                </Typography>
              </Typography>
              <img
                src={require('../assets/logo-ags.png')}
                alt="Logo Asociación de Golf del Sur"
                style={{ maxWidth: '80%', height: 'auto' }}
              />
              <Typography
                  component="span"
                  variant="h3"
                  sx={{
                    color: 'secondary.main',
                    fontWeight: 'bold',
                  }}
                >
                  {'\n'}#ElGolfNosUne
                </Typography>
            </Box>
          </Grid>

          {/* Right side - Registration Form */}
          <Grid item xs={12} md={6}>
            <Box
              sx={{
                height: '100%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <Paper
                elevation={3}
                sx={{
                  p: 4,
                  width: '100%',
                  maxWidth: 400,
                  bgcolor: 'rgba(255, 255, 255, 0.9)',
                  backdropFilter: 'blur(10px)',
                }}
              >
                <Typography variant="h4" component="h2" gutterBottom align="center">
                  Crea tu cuenta
                </Typography>
                <Typography variant="body1" color="text.secondary" gutterBottom align="center">
                  Llena tus datos para registrarte
                </Typography>

                <Box component="form" onSubmit={handleSubmit} sx={{ mt: 3 }}>
                  {error && (
                    <Alert severity="error" sx={{ mb: 2 }}>
                      {error}
                    </Alert>
                  )}
                  <Grid container spacing={2}>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        required
                        fullWidth
                        id="firstName"
                        label="Nombre"
                        name="firstName"
                        autoComplete="given-name"
                        value={formData.firstName}
                        onChange={handleChange}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        required
                        fullWidth
                        id="lastName"
                        label="Apellidos"
                        name="lastName"
                        autoComplete="family-name"
                        value={formData.lastName}
                        onChange={handleChange}
                      />
                    </Grid>
                  </Grid>
                  <TextField
                    margin="normal"
                    required
                    fullWidth
                    id="email"
                    label="Email"
                    name="email"
                    autoComplete="email"
                    value={formData.email}
                    onChange={handleChange}
                  />
                  <TextField
                    margin="normal"
                    required
                    fullWidth
                    name="password"
                    label="Contraseña"
                    type="password"
                    id="password"
                    autoComplete="new-password"
                    value={formData.password}
                    onChange={handleChange}
                  />
                  <TextField
                    margin="normal"
                    required
                    fullWidth
                    name="confirmPassword"
                    label="Confirmar Contraseña"
                    type="password"
                    id="confirmPassword"
                    value={formData.confirmPassword}
                    onChange={handleChange}
                  />
                  <Button
                    type="submit"
                    fullWidth
                    variant="contained"
                    disabled={loading}
                    sx={{ mt: 3, mb: 2 }}
                  >
                    {loading ? (
                      <CircularProgress size={24} color="inherit" />
                    ) : (
                      'Crear Cuenta'
                    )}
                  </Button>
                  <Box sx={{ textAlign: 'center' }}>
                    <Typography variant="body2">
                      Ya tienes una cuenta?{' '}
                      <Link
                        component="button"
                        variant="body2"
                        onClick={() => navigate('/')}
                        sx={{ cursor: 'pointer' }}
                      >
                        Inicia Sesión
                      </Link>
                    </Typography>
                  </Box>
                </Box>
              </Paper>
            </Box>
          </Grid>
        </Grid>
      </Container>
    </>
  );
};

export default Register; 